import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { apiFetch } from "../lib/api.js";
import { IllustrationAscent } from "../components/illustrations.jsx";
import SpineTip from "../components/SpineTip.jsx";

async function startSession(type, navigate) {
  const session = await apiFetch("/sessions", { method: "POST", body: { type } });
  if (session.starterMessage) {
    sessionStorage.setItem(`coach-starter-${session.id}`, session.starterMessage);
  }
  navigate(`/coach/${session.id}`);
}

function WelcomeScreen({ navigate }) {
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);

  async function handleStart() {
    setStarting(true);
    setError(null);
    try {
      await startSession("first", navigate);
    } catch (e) {
      setError(e.message);
      setStarting(false);
    }
  }

  return (
    <div className="page welcome-screen">
      <div className="kicker" style={{ marginBottom: 32 }}>
        <span className="the-line" />
        YOUR CORNER
      </div>
      <h1 className="welcome-screen-heading">You've seen where you stand.</h1>
      <p className="welcome-screen-copy">Now let's claim some ground.</p>
      <p className="welcome-screen-sub">
        Your first session with your corner - about an hour.
      </p>
      <div style={{ marginTop: 40 }}>
        <button className="btn-primary" onClick={handleStart} disabled={starting}>
          Claim your ground
        </button>
      </div>
      {error && <p style={{ color: "var(--error)", marginTop: 16 }}>{error}</p>}
    </div>
  );
}

const NUMBER_WORDS = ["no", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve"];

function numberWord(n) {
  return NUMBER_WORDS[n] || String(n);
}

// The returning-man welcome line - canon/11 section 6, verbatim, with only
// the counts adapted to this member's numbers.
function idleCopy(weeks, held) {
  const weekPhrase = weeks === 1 ? "one week" : `${numberWord(weeks)} weeks`;
  const heldPhrase = held === 0 ? "none of them" : held === 1 ? "one of them" : `${numberWord(held)} of them`;
  return `Welcome back. It's been ${weekPhrase}. Your floor held ${heldPhrase} - no shame, that is what the floor is for. Want to see what's moved? Retake your Standing Check - five minutes - or go straight to a Monday review.`;
}

// Canon/11 section 5 prompts, used by the one-line block when THE ONE is
// not set ("If nothing set: the next-action prompt").
const STATE_PROMPTS = {
  no_check: "See where you stand - the Standing Check. Five minutes.",
  claim_ground: "Claim your ground. Your first session with your corner - about an hour.",
  weekly_due: "It's Monday. Run your review.",
  floor_breach: "Rough round. Get you up - the reset.",
  quarter_end: "Close the quarter. The quarter review.",
};

function groundSlug(domain) {
  return domain.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

function NextActionHero({ nextAction, onMarkTheOne, marking, navigate }) {
  if (!nextAction || !nextAction.state) return null;

  const kicker = (
    <div className="kicker">
      <span className="the-line" />
      NEXT
    </div>
  );

  if (nextAction.state === "no_check") {
    return (
      <div className="hero-card">
        {kicker}
        <p className="hero-card-copy">See where you stand - the Standing Check. Five minutes.</p>
        <div className="hero-card-actions">
          <Link href="/diagnostic" className="btn-primary" style={{ textDecoration: "none" }}>
            Take the Standing Check
          </Link>
        </div>
      </div>
    );
  }

  if (nextAction.state === "weekly_due") {
    return (
      <div className="hero-card">
        {kicker}
        <p className="hero-card-copy">It's Monday. Run your review.</p>
        <div className="hero-card-actions">
          <button className="btn-primary" onClick={() => startSession("weekly", navigate)}>
            Run your review
          </button>
        </div>
      </div>
    );
  }

  if (nextAction.state === "the_one") {
    return (
      <div className="hero-card">
        {kicker}
        <div className="hero-card-the-one">
          <p className="hero-card-copy" style={{ marginBottom: 0 }}>{nextAction.item}</p>
          <span className="hero-card-the-one-tag">THE ONE</span>
        </div>
        <div className="hero-card-actions">
          <button className="btn-primary" disabled={marking} onClick={() => onMarkTheOne("done")}>
            Done
          </button>
          <button className="btn-secondary" disabled={marking} onClick={() => onMarkTheOne("carried")}>
            Carried
          </button>
        </div>
      </div>
    );
  }

  if (nextAction.state === "floor_breach") {
    return (
      <div className="hero-card bronze">
        {kicker}
        <p className="hero-card-copy">Rough round. Get you up - the reset.</p>
        <div className="hero-card-actions">
          <button className="btn-primary" onClick={() => startSession("reset", navigate)}>
            Get me up
          </button>
        </div>
      </div>
    );
  }

  if (nextAction.state === "quarter_end") {
    return (
      <div className="hero-card">
        {kicker}
        <p className="hero-card-copy">Close the quarter. The quarter review.</p>
        <div className="hero-card-actions">
          <button className="btn-primary" onClick={() => startSession("quarter", navigate)}>
            Close the quarter
          </button>
        </div>
      </div>
    );
  }

  if (nextAction.state === "idle") {
    // Shame-free returning-member welcome. Copy is canon/11 section 6,
    // verbatim, with only the counts adapted to the member's numbers.
    return (
      <div className="hero-card">
        {kicker}
        <p className="hero-card-copy">
          {idleCopy(nextAction.weeksIdle, nextAction.floorHeldWeeks ?? 0)}
        </p>
        <div className="hero-card-actions">
          <Link href="/diagnostic" className="btn-secondary" style={{ textDecoration: "none" }}>
            Retake the Standing Check
          </Link>
          <button className="btn-primary" onClick={() => startSession("weekly", navigate)}>
            Run your review
          </button>
        </div>
      </div>
    );
  }

  return null;
}

function SpineSection({ kickerLabel, title, tipId, tipText, dismissedTips, onDismiss, children }) {
  return (
    <section className="spine-section">
      <div className="kicker" style={{ marginBottom: 10 }}>
        <span className="the-line" />
        {kickerLabel}
      </div>
      {title && <h2 style={{ marginBottom: 12 }}>{title}</h2>}
      <SpineTip tipId={tipId} text={tipText} dismissedTips={dismissedTips} onDismiss={onDismiss} />
      {children}
    </section>
  );
}

// Block one-liners - canon/11 section 10.1, verbatim.
const ONE_LINE_TIP = "Your week in one line: what today asks of you, and whether your floor is intact.";
const GROUND_TIP = "Six grounds. Three states: Solid, Unsteady, Unclaimed. Tap one to see its goals.";
const BUILDING_TIP = "Your vision, and the goals under it. Up to three at a time, each standing on a ground.";
const WEEK_TIP = "Your Big 3 for the week. THE ONE at the top is your highest priority - mark it done or carried each day.";
const RECORD_TIP = "Weeks held. Not a streak - a record. A week you drop and recover still counts as a week you got up.";

// The tour - canon/11 section 10.2, verbatim. Three tips, first login only.
const TOUR_GROUND_TIP = "This is your ground. Six areas, honestly stated. Unclaimed is not shame - it is ground to take.";
const TOUR_BUILDING_TIP = "This is what you're building. Your vision, and up to three goals standing on it.";
const TOUR_RECORD_TIP = "This is your record. Weeks held, not streaks. Getting up counts.";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [content, setContent] = useState(null);
  const [error, setError] = useState(null);
  const [marking, setMarking] = useState(false);
  const [, navigate] = useLocation();

  useEffect(() => {
    Promise.all([apiFetch("/dashboard"), apiFetch("/content/diagnostic")])
      .then(([d, c]) => {
        setData(d);
        setContent(c);
      })
      .catch((e) => setError(e.message));
  }, []);

  async function handleMarkTheOne(status) {
    setMarking(true);
    try {
      await apiFetch("/dashboard/the-one", { method: "POST", body: { status } });
      const fresh = await apiFetch("/dashboard");
      setData(fresh);
    } catch (e) {
      setError(e.message);
    } finally {
      setMarking(false);
    }
  }

  async function handleDismissTip(tipId) {
    setData((prev) => (prev ? { ...prev, dismissedTips: [...(prev.dismissedTips || []), tipId] } : prev));
    try {
      await apiFetch("/dashboard/dismiss-tip", { method: "POST", body: { tipId } });
    } catch (e) {
      /* non-critical - worst case the tip reappears next load */
    }
  }

  if (error) return <div className="page">{error}</div>;
  if (!data || !content) return null;

  if (data.nextAction.state === "claim_ground") {
    return <WelcomeScreen navigate={navigate} />;
  }

  const dismissedTips = data.dismissedTips || [];
  const domains = content.domains || [];
  const bandsByDomain = new Map((data.domainBands || []).map((d) => [d.domain, d]));
  const hasStandingCheck = (data.domainBands || []).length > 0;
  const big3 = data.big3 || [];
  const theOne = big3[0];
  const theOneMarkedToday =
    data.theOneMarkedAt && new Date(data.theOneMarkedAt).toDateString() === new Date().toDateString()
      ? data.theOneStatus
      : null;

  const goalsByDomain = new Map();
  (data.quarterlyGoals || []).forEach((g) => {
    const status = (g.status || "").toLowerCase();
    if (status.includes("done") || status.includes("dropped")) return;
    const domain = domains.find((d) => d.toLowerCase() === (g.area || "").toLowerCase()) || g.area || "Unassigned";
    const list = goalsByDomain.get(domain) || [];
    if (list.length < 3) {
      list.push(g);
      goalsByDomain.set(domain, list);
    }
  });

  // Canon/11 section 4 block 1: "Week 27. THE ONE today: send the investor
  // update. Floor: intact." One sentence, mono. If nothing set, the block
  // shows the next-action prompt (section 5) instead. When there is no
  // prompt either (state null - e.g. a weekend with THE ONE already
  // marked), the line falls back to the week + floor segments alone.
  const oneLineSegments = [
    data.weekNumber != null ? `Week ${data.weekNumber}.` : null,
    theOne ? `THE ONE today: ${theOne.item}.` : null,
    data.floorStatus ? `Floor: ${data.floorStatus}.` : null,
  ].filter(Boolean);
  let oneLine;
  if (theOne) {
    oneLine = oneLineSegments.join(" ");
  } else if (data.nextAction.state === "idle") {
    oneLine = idleCopy(data.nextAction.weeksIdle, data.nextAction.floorHeldWeeks ?? 0);
  } else if (STATE_PROMPTS[data.nextAction.state]) {
    oneLine = STATE_PROMPTS[data.nextAction.state];
  } else {
    oneLine = oneLineSegments.join(" ");
  }

  return (
    <div className="page-wide">
      <div className="kicker" style={{ marginBottom: 12 }}>
        <span className="the-line" />
        HOME
      </div>
      <h1 style={{ marginBottom: 24 }}>Where things stand</h1>

      <NextActionHero
        nextAction={data.nextAction}
        onMarkTheOne={handleMarkTheOne}
        marking={marking}
        navigate={navigate}
      />

      <div className="illustration-hero-wrap">
        <IllustrationAscent className="illustration-hero" />
      </div>

      <div className="spine">
        {oneLine && (
          <SpineSection
            kickerLabel="THE ONE LINE"
            tipId="one-line"
            tipText={ONE_LINE_TIP}
            dismissedTips={dismissedTips}
            onDismiss={handleDismissTip}
          >
            <p className="mono status-line">{oneLine}</p>
          </SpineSection>
        )}

        <SpineSection
          kickerLabel="YOUR GROUND"
          title="Your six grounds"
          tipId="your-ground"
          tipText={GROUND_TIP}
          dismissedTips={dismissedTips}
          onDismiss={handleDismissTip}
        >
          <SpineTip
            tipId="tour-your-ground"
            text={TOUR_GROUND_TIP}
            dismissedTips={dismissedTips}
            onDismiss={handleDismissTip}
            tour
          />
          {!hasStandingCheck ? (
            <p className="empty-state">The Standing Check maps this. Five minutes - see where you actually stand.</p>
          ) : (
            <div className="ground-row">
              {domains.map((domain) => {
                const band = bandsByDomain.get(domain);
                const label = band ? band.band : "Unclaimed";
                return (
                  <Link key={domain} href={`/ground#ground-${groundSlug(domain)}`} className="ground-tile">
                    <span className="ground-tile-name">{domain}</span>
                    <span className={`band-label band-${label}`}>{label}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </SpineSection>

        <SpineSection
          kickerLabel="WHAT YOU'RE BUILDING"
          title="Your vision and your goals"
          tipId="what-youre-building"
          tipText={BUILDING_TIP}
          dismissedTips={dismissedTips}
          onDismiss={handleDismissTip}
        >
          <SpineTip
            tipId="tour-building"
            text={TOUR_BUILDING_TIP}
            dismissedTips={dismissedTips}
            onDismiss={handleDismissTip}
            tour
          />
          {data.visionSketch ? (
            <p style={{ lineHeight: 1.6, marginBottom: 20 }}>{data.visionSketch}</p>
          ) : (
            <p className="empty-state" style={{ marginBottom: 20 }}>
              No vision sketch yet. Your corner builds it with you - about an hour.
            </p>
          )}
          {goalsByDomain.size === 0 ? (
            <p className="empty-state">No goals yet. They come out of your vision.</p>
          ) : (
            <div className="hairline-list">
              {Array.from(goalsByDomain.entries()).map(([domain, goals]) =>
                goals.map((g, i) => (
                  <div key={`${domain}-${i}`} style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                    <span>{g.goal}</span>
                    <span className="mono" style={{ color: "var(--bronze-lit)", whiteSpace: "nowrap" }}>{domain}</span>
                  </div>
                ))
              )}
            </div>
          )}
        </SpineSection>

        <SpineSection
          kickerLabel="THIS WEEK"
          title="This week's Big 3"
          tipId="this-week"
          tipText={WEEK_TIP}
          dismissedTips={dismissedTips}
          onDismiss={handleDismissTip}
        >
          {big3.length === 0 ? (
            <p className="empty-state">No Big 3 yet. Monday fixes that.</p>
          ) : (
            <>
              <div className="hairline-list" style={{ marginBottom: 20 }}>
                {big3.map((b, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
                    <span style={i === 0 ? { color: "var(--bronze-lit)", fontWeight: 600 } : undefined}>{b.item}</span>
                    <span style={{ display: "flex", gap: 10, alignItems: "baseline" }}>
                      {i === 0 && <span className="hero-card-the-one-tag">THE ONE</span>}
                      {b.percent != null && <span className="mono" style={{ color: "var(--muted)" }}>{b.percent}%</span>}
                    </span>
                  </div>
                ))}
              </div>
              {theOne && (
                <div className="hero-card-actions">
                  <button
                    className="btn-primary"
                    disabled={marking || theOneMarkedToday === "done"}
                    onClick={() => handleMarkTheOne("done")}
                  >
                    Done
                  </button>
                  <button
                    className="btn-secondary"
                    disabled={marking || theOneMarkedToday === "carried"}
                    onClick={() => handleMarkTheOne("carried")}
                  >
                    Carried
                  </button>
                </div>
              )}
            </>
          )}
        </SpineSection>

        <SpineSection
          kickerLabel="THE RECORD"
          title="Weeks held so far"
          // Canon/11 section 7: weeks held is "explained the first time a
          // week completes" - so the one-liner only fires once at least one
          // week has resolved on the record, not on first sight of the block.
          tipId={data.weekMarks && data.weekMarks.length > 0 ? "the-record" : null}
          tipText={RECORD_TIP}
          dismissedTips={dismissedTips}
          onDismiss={handleDismissTip}
        >
          <SpineTip
            tipId="tour-record"
            text={TOUR_RECORD_TIP}
            dismissedTips={dismissedTips}
            onDismiss={handleDismissTip}
            tour
          />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 16 }}>
            <span style={{ color: "var(--muted)" }}>Weeks held</span>
            <span className="mono" style={{ fontSize: 28 }}>{data.weeksHeld}</span>
          </div>
          {data.weekMarks && data.weekMarks.length > 0 ? (
            <div className="record-row">
              {data.weekMarks.map((w) => (
                <span key={w.weekKey} className={`record-mark record-mark-${w.state}`} title={w.weekKey} />
              ))}
            </div>
          ) : (
            <p className="empty-state">Nothing on the record yet. The first week starts on Monday.</p>
          )}
        </SpineSection>
      </div>

      <div style={{ marginTop: 32 }}>
        <Link href="/coach" className="btn-primary" style={{ display: "inline-block", textDecoration: "none" }}>
          Talk to your corner
        </Link>
      </div>
    </div>
  );
}
